import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { HeartHandshake, Menu, Search, Globe, Heart } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { useApp } from "../context/AppContext";

const NAV = [
  ["home", "/"], ["about", "/about"], ["work", "/work"], ["projects", "/projects"],
  ["articles", "/articles"], ["gallery", "/gallery"], ["greetings", "/greetings"],
  ["news", "/news"], ["register", "/register"], ["contact", "/contact"],
];

export default function Header() {
  const { t, lang, setLang, site } = useApp();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const items = site?.nav_items?.length
    ? site.nav_items
    : NAV.map(([key, to]) => ({ id: key, label_en: t(key), label_hi: null, url: to, external: false, visible: true }));
  const visible = items.filter((i) => i.visible !== false);
  const main = visible.slice(0, 8);
  const extra = visible.slice(8);
  const slug = (it) => (it.label_en || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const label = (it) => (lang === "hi" && it.label_hi ? it.label_hi : it.label_en);

  const NavItem = ({ item, tid, onClick, mobile }) => {
    const base = mobile
      ? "rounded-lg px-4 py-3 text-base font-semibold transition-colors"
      : "whitespace-nowrap text-sm font-semibold transition-colors duration-200 hover:text-saffron-600";
    if (item.external) {
      return <a href={item.url} target="_blank" rel="noreferrer" data-testid={tid} onClick={onClick}
        className={`${base} ${mobile ? "text-navy hover:bg-white" : "text-navy"}`}>{label(item)}</a>;
    }
    return (
      <NavLink to={item.url} end={item.url === "/"} onClick={onClick} data-testid={tid}
        className={({ isActive }) => `${base} ${isActive ? (mobile ? "bg-saffron-100 text-navy" : "text-saffron-600") : "text-navy"}`}>
        {label(item)}
      </NavLink>
    );
  };

  const linkCls = ({ isActive }) =>
    `whitespace-nowrap text-sm font-semibold transition-colors duration-200 hover:text-saffron-600 ${isActive ? "text-saffron-600" : "text-navy"}`;

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 border-b border-gray-200/60 bg-white/80 backdrop-blur-xl">
      <div className="bg-navy py-1.5">
        <div className="container-ssf flex items-center justify-between text-[11px] font-medium text-slate-300">
          <span>Prayagraj (Allahabad), Uttar Pradesh — 211015</span>
          <span className="hidden sm:block">UDYAM-UP-03-0051126 &nbsp;|&nbsp; NGO DARPAN: UP/2012/03384132</span>
        </div>
      </div>
      <div className="container-ssf flex h-16 items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link" aria-label="Sahaj Sarathi Foundation home">
          <img src="/logo.png" alt="Sahaj Sarathi Foundation logo" className="h-12 w-12 rounded-full object-contain ring-2 ring-saffron-500/50" />
          <span className="leading-tight">
            <span className="block font-display text-lg font-bold tracking-tight text-navy">Sahaj Sarathi Foundation</span>
            <span className="block text-[11px] font-medium text-slate-500">Registered NGO</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-5 xl:flex" aria-label="Primary">
          {main.map((item) => <NavItem key={item.id || item.url} item={item} tid={`nav-${slug(item)}`} />)}
        </nav>

        <div className="flex items-center gap-2">
          <button data-testid="search-btn" onClick={() => navigate("/search")}
            className="hidden h-9 w-9 items-center justify-center rounded-full text-navy transition-colors hover:bg-saffron-100 sm:flex" aria-label="Search">
            <Search size={18} />
          </button>
          <button data-testid="lang-toggle" onClick={() => setLang(lang === "en" ? "hi" : "en")}
            className="flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-bold text-navy transition-colors hover:border-saffron-500">
            <Globe size={14} /> {t("language")}
          </button>
          <NavLink to="/superadmin" data-testid="nav-superadmin" aria-label="Open Superadmin login"
            className={({ isActive }) => `hidden items-center whitespace-nowrap rounded-full border px-4 py-2 text-sm font-bold transition-colors duration-200 xl:inline-flex ${isActive ? "border-saffron-500 bg-saffron-100 text-navy" : "border-navy/20 text-navy hover:border-saffron-500 hover:bg-saffron-100/60"}`}>
            {lang === "hi" ? "सुपरएडमिन" : "Superadmin"}
          </NavLink>
          <Link data-testid="donate-now-btn" to="/donate"
            className="hidden items-center gap-2 rounded-full bg-saffron-500 px-5 py-2.5 text-sm font-bold text-navy shadow-md shadow-saffron-500/25 transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-saffron-600 sm:inline-flex">
            <Heart size={15} /> {t("donate")}
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="flex h-10 w-10 items-center justify-center rounded-full text-navy xl:hidden" aria-label="Menu">
                <Menu size={22} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 overflow-y-auto bg-ivory">
              <nav className="mt-8 flex flex-col gap-1" aria-label="Mobile">
                {visible.map((item) => (
                  <NavItem key={item.id || item.url} item={item} mobile tid={`mnav-${slug(item)}`} onClick={() => setOpen(false)} />
                ))}
                <Link to="/superadmin" onClick={() => setOpen(false)} data-testid="mnav-superadmin"
                  className="mt-2 rounded-full border border-navy/20 px-4 py-3 text-center text-base font-bold text-navy transition-colors hover:border-saffron-500 hover:bg-white">
                  {lang === "hi" ? "सुपरएडमिन" : "Superadmin"}
                </Link>
                <Link to="/donate" onClick={() => setOpen(false)} data-testid="mnav-donate" className="btn-primary mt-4">
                  <Heart size={16} /> {t("donate")}
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <div className="border-t border-gray-100 xl:hidden">
        <div className="container-ssf flex gap-4 overflow-x-auto py-2 [scrollbar-width:none]">
          {extra.map((item) => <NavItem key={item.id || item.url} item={item} tid={`nav2-${slug(item)}`} />)}
          <NavLink to="/superadmin" data-testid="tablet-nav-superadmin"
            className={({ isActive }) => `whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-bold transition-colors duration-200 ${isActive ? "border-saffron-500 bg-saffron-100 text-navy" : "border-navy/20 text-navy hover:border-saffron-500"}`}>
            {lang === "hi" ? "सुपरएडमिन" : "Superadmin"}
          </NavLink>
        </div>
      </div>
    </header>
  );
}
